import React, { useState, useMemo, useEffect } from 'react';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { annuityPreferenceOf, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import AnnuityPreferenceGate from '../../components/strategy/AnnuityPreferenceGate.tsx';
import { cashflowCoveredNote, CASHFLOW_COVERED_LABEL, GOAL_CHIP_LABELS, coveredIsBestEstate } from '../../strategy/cashflowCoveredGoal.ts';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { ChevronLeft, ArrowRight, Home, PiggyBank, TrendingUp, AlertTriangle, Scale, Wallet } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromCandidateId } from '../../strategy/strategyRisks.ts';
import { alternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { buildDownsizerSoaSnapshot } from './dzSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';

import { Card, GradientText, CurrencyInput, InfoIcon, FeeExplanationAccordion, Button, IconTile } from '../../components/ui/index.tsx';
import { getDownsizeBeforeAfter, runDownsizeProjections, buildDownsizerSuggestions, generateDownsizerCandidates, dzEngineSetupFromGoals, isDzSensitivity } from './downsizerScenario.tsx';
import type { Financials, ProjectionYear, DownsizeDetails, DownsizerSuggestionsResult, DzEngineSetup } from './downsizerScenario.tsx';
import { enrichStrategiesWithRecommendationLabels, buildRecommendedOptimalStrategies, mergeGoalLeaderStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { buildGoalCards, visibleGoalCards, emptyGoalLine, type GoalFocus } from '../../strategy/engineGoalCards.ts';
import GoalChipRow from '../../components/strategy/GoalChipRow.tsx';
import AllStrategiesModal from '../../components/strategy/AllStrategiesModal.tsx';
import EngineStrategyCard from '../../components/strategy/EngineStrategyCard.tsx';
import SameAsBestEstateNote from '../../components/strategy/SameAsBestEstateNote.tsx';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';
import DzStayPutHero from './DzStayPutHero.tsx';
import DzAnalysisSetupModal from './DzAnalysisSetupModal.tsx';
import { DzInBrief, nilResidualAnnuityText, type DzInBriefAnnuity } from './dzInBrief.tsx';
import { annuityHorizonFlag, type HorizonFactFind } from '../agedCare/strategyHorizonFlags.ts';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import FinancialImpactPanel from '../agedCare/FinancialImpactPanel.tsx';
import type { FinancialPosition } from '../agedCare/FinancialImpactPanel.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import type { MoneyFlowStep } from '../../components/narrative/PlainWords.tsx';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../../constants/index.ts';
import { FUNERAL_BOND_LIMIT, GENERAL_TRANSFER_BALANCE_CAP } from '../../constants/index.ts';
import DownsizerRulesAndInsights from '../../components/strategy/DownsizerRulesAndInsights.tsx';
import DownsizerPlanRecap from './DownsizerPlanRecap.tsx';
import { ProceedsExemptionNote, DownsizerContributionNote, SellAndRentNote, DownsizingTrapNote } from './DownsizerFieldNotes.tsx';
import { ModeChooser } from '../../components/ui/index.tsx';
import type { DownsizerInsightFinancials } from './downsizerTypes.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import { hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import StampDutyFields from '../../components/ui/StampDutyFields.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import PlanSummaryPanel from '../agedCare/PlanSummaryPanel.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { FundingRunway } from '../../components/narrative/index.tsx';
import DebtPayoffStrategy from '../agedCare/DebtPayoffStrategy.tsx';
import { applyDebtPayoffToFactFind, type DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import ProceedsDeploymentFields, { deploymentAnnuityTerms } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import type { ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import AssetFundingAllocator from '../../components/strategy/AssetFundingAllocator.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

interface ExplainSection {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  summary: string;
  details: React.ReactNode;
  applies: boolean;
}

const DZ_EXPLAIN_SECTIONS: ExplainSection[] = [
  { id: 'what', icon: Home, title: 'What downsizing is', summary: 'Selling your home and moving somewhere that suits you better — smaller or easier to run. It does not have to be cheaper.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>Downsizing means selling your current home and moving somewhere that suits you better: smaller, easier to look after, closer to family or services. <strong>It does not have to be cheaper</strong>, and often is not — a low-maintenance apartment in the same suburb can cost as much as the house you are leaving, or more.</p>
      <p><strong>Why people actually move</strong> matters here, because it changes which answer is right:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>To free up money</strong> for retirement — buy cheaper, and the difference becomes cash you can use.</li>
        <li><strong>To reduce the upkeep</strong> — the garden, the stairs, the maintenance. The money may not change at all.</li>
        <li><strong>To move somewhere better</strong> — closer to family, services or care. That can cost more than the home you sell.</li>
      </ul>
      <p>All three are legitimate, and they lead to different financial outcomes. The next two sections are the two directions this can go — and they are opposites.</p>
    </div>
  ), applies: true },
  { id: 'trap', icon: AlertTriangle, title: 'Buying CHEAPER: the Age Pension "downsizing trap"', summary: 'Your home is exempt from the assets test whatever it is worth, but the cash you free up is counted — so buying cheaper can reduce your pension.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>Your home doesn&apos;t count towards the Age Pension assets test, <strong>no matter its value</strong>. When you sell and buy something cheaper, the money you free up <strong>becomes an assessable asset</strong> — and is deemed to earn income on top. That can reduce your Age Pension, sometimes to nil.</p>
      <p>It can still be the right move: freed-up cash is money you can actually use, and a pension you lose is not the only thing that matters. But you should see the size of the effect before deciding, which is what the calculator is for.</p>
    </div>
  ), applies: true },
  /*
   * THE OPPOSITE OF THE TRAP, and the module never said it (owner, 2026-08-28).
   *
   * Every word of this explainer assumed downsizing means buying cheaper, so the only pension story
   * it could tell was the trap. But the exemption that CAUSES the trap runs both ways: the home is
   * exempt at any value, so moving counted money INTO a dearer home lifts the pension by the same
   * mechanism that buying cheaper lowers it.
   *
   * Measured against the engine on a $1m homeowner with $500k in savings, a $1.15m purchase took the
   * pension from $17,300 to $29,418 — see server/.../downsizerNewHomePrice.test.ts. That is a real
   * strategy an adviser would consider, and it is exactly the person who moves for MAINTENANCE
   * rather than money who is most likely to be buying at or above what they sell for.
   *
   * It is stated with its costs, not as a trick. The money is in the house, it is not deprivation,
   * and it only helps someone the assets test is actually biting.
   */
  { id: 'buyup', icon: TrendingUp, title: 'Buying SMALLER BUT DEARER: the opposite of the trap', summary: 'The home is exempt at any value — so moving money into a more expensive home can INCREASE your Age Pension.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>The rule that causes the trap works both ways. If the home is exempt <strong>whatever it is worth</strong>, then money moved out of your savings and into a dearer home <strong>stops being counted</strong> — and the Age Pension can go <strong>up</strong>.</p>
      <p>That is not a loophole, and it is worth being clear about why. If you <strong>give money away</strong>, Centrelink does not simply stop counting it — anything above $10,000 in a year (or $30,000 over five years) is treated as though you still own it <strong>for the next five years</strong>. That rule is called <em>deprivation</em>, and it exists to stop people giving assets away to qualify for a pension.</p>
      <p><strong>Buying a home is not giving money away.</strong> You still own every dollar — it is simply held in a house instead of a bank account. So nothing is treated as deprived, and that five-year rule does not apply here at all. It is a recognised strategy, and it suits the person who is moving for the upkeep rather than the money: a smaller, newer, easier place that happens to cost more than the house they are leaving.</p>
      <p><strong>What it costs you, and this is the real trade-off:</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>The money is in the house.</strong> You cannot spend it, and getting it back means selling again or borrowing against it.</li>
        <li><strong>It only helps if the assets test is what is limiting your pension.</strong> On a full pension already, or limited by the income test, there may be nothing to gain.</li>
        <li><strong>Aged care assesses the home differently.</strong> If residential care is on the horizon, a larger home is counted there up to a cap — so this can help the pension and cost you later.</li>
        <li><strong>Buying and selling costs are real</strong> and are paid whichever direction you move.</li>
      </ul>
      <p>The Suggestion Engine models a cheaper, a similar-priced and a dearer move side by side, so you can see which way your own numbers fall rather than assuming.</p>
    </div>
  ), applies: true },
  { id: 'exemption', icon: Scale, title: 'The 24-month proceeds exemption', summary: 'Proceeds you intend to use for a new home are exempt from the assets test for up to 24 months (longer if delayed), and deemed at the lower rate.', details: <p className="text-gray-700 leading-relaxed">If you sell before you&apos;ve bought or built your new home, the portion of the proceeds you <strong>intend to use for the new home</strong> is exempt from the assets test for up to <strong>24 months</strong> (and up to 36 months if you&apos;re delayed for reasons outside your control). During that time it&apos;s counted under the income test at the <strong>lower deeming rate</strong> only. Once you buy, the new home is exempt. Tell Services Australia within 14 days of selling.</p>, applies: true },
  { id: 'super', icon: PiggyBank, title: 'Topping up super (downsizer contribution)', summary: 'If you are 55+, you can put up to $300,000 each from the sale into super — outside the normal caps, but with some important rules.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>If you&apos;re <strong>55 or older</strong> and you (or your spouse) owned the home for <strong>10+ years</strong>, you can contribute up to <strong>$300,000 each</strong> ($600,000 for a couple) from the sale into super as a <strong>downsizer contribution</strong>, within <strong>90 days</strong> of settlement. No work test, no upper age limit, and you don&apos;t have to actually buy a new (or cheaper) home.</p>
      <p><strong>The rules worth knowing:</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Once only.</strong> You can use the downsizer contribution just once, ever, from a single home sale.</li>
        <li><strong>Doesn&apos;t use your contribution caps.</strong> It&apos;s on top of your normal concessional/non-concessional caps, and you can make it even if your total super balance is already large.</li>
        <li><strong>But the transfer balance cap still applies.</strong> Money held in a <em>retirement pension</em> account inside super pays <strong>no tax at all</strong> on its earnings — so there is a lifetime limit on how much you are allowed to move into one. That limit is the <strong>transfer balance cap</strong>, currently <strong>{formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}</strong>, and a downsizer contribution counts towards it like any other money. Anything above the cap has to stay in an <em>accumulation</em> account instead, where earnings are taxed at 15% — still low, just not free.</li>
        <li><strong>Very large balances:</strong> from 1 July 2026 an extra 15% tax applies to earnings on the part of any super balance over $3 million (Division 296).</li>
      </ul>
      <p><strong>Why bother?</strong> Inside super, investment earnings are taxed at most 15% (and 0% in pension phase) — usually far less than in your own name. The trade-off: once you&apos;re Age-Pension age, super counts in the means test, so moving home equity (exempt) into super (assessable) can reduce your pension. The Suggestion Engine weighs that tax saving against the pension cost on your own figures, so you can see which way it lands rather than guessing.</p>
    </div>
  ), applies: true },
  { id: 'cgt', icon: Wallet, title: 'Capital Gains Tax', summary: 'Selling your main residence is usually CGT-free.', details: <p className="text-gray-700 leading-relaxed">Your main residence is generally <strong>exempt from Capital Gains Tax</strong>. If part of the home was used to produce income (e.g. rented out or a home business), a partial CGT liability can apply. Confirm with your accountant.</p>, applies: true },
];

interface DownsizerExplainedProps {
  setActivePage: (page: string) => void;
  onContinue: () => void;
}

const DownsizerExplained = ({ setActivePage, onContinue }: DownsizerExplainedProps) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggle = (id: string) => setOpenAccordion(openAccordion === id ? null : id);
  return (
    <div className="max-w-6xl mx-auto">
      <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setActivePage('Dashboard')} className="mb-4 -ml-2">Back to Dashboard</Button>
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <IconTile icon={Home} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900"><GradientText>Downsizing Your Home</GradientText></h1>
        </div>
        <p className="text-lg text-slate-700 leading-relaxed mb-3">Selling the family home and moving somewhere that suits you better — smaller, easier to run, closer to family. It might free up money, or it might not; either way it changes your Age Pension and where your wealth sits. Let&apos;s model it so there are no surprises.</p>
        <p className="text-sm text-slate-500">General information and modelling only. You can confirm pension impacts free with Services Australia&apos;s Financial Information Service, and your accountant should look at any capital gains tax before you sell.</p>
      </div>
      <div className="space-y-4 mb-8">
        {DZ_EXPLAIN_SECTIONS.map((fee) => (
          <FeeExplanationAccordion key={fee.id} {...fee} isOpen={openAccordion === fee.id} onToggle={() => toggle(fee.id)} factFind={{}} />
        ))}
      </div>
      <div className="text-center">
        <Button onClick={onContinue} variant="brand" size="lg" iconRight={ArrowRight}>Continue to calculator</Button>
      </div>
    </div>
  );
};

interface DzInputs {
  salePrice: number;
  sellingCosts: number;
  sellAndRent: boolean;
  /** Weekly rent where the plan rents instead of buying — charged as a living cost, with Rent Assistance assessed on it. */
  rentPerWeek: number;
  newHomePurchasePrice: number;
  /** Duty on the new home and the state it is in — estimated from the state's scale, overwritable. */
  stampDuty: number;
  newHomeState: string;
  downsizerContribution: number;
  partnerDownsizerContribution: number;
  commenceIncomeStream: boolean;
  giftingAmount: number;
  funeralBondPurchase: number;
  deployment: ProceedsDeploymentValue;
  buyUpFunding: Record<string, number>;
  buyUpCgtOverrides: Record<string, number | null>;
}

/** A leader card from the Suggestion Engine — EvaluatedStrategy plus the fields this module reads off it. */
export interface DzStrategyView extends EvaluatedStrategy {
  id: string;
  description?: string;
  inputs?: DownsizeDetails;
  projections?: ProjectionYear[];
  deploymentBreakdown?: Array<{ label: string; amount: number }>;
  /** Weekly rent the plan pays where it rents instead of buying (the evaluator's figure). */
  rentPerWeek?: number;
}

interface SeView {
  name?: string;
  before: Financials;
  after: Financials;
  projections: ProjectionYear[];
  hasDownsize: boolean;
  salePrice: number;
  /** Agent, marketing and legal costs of the sale — carried here so the plan summary quotes the
   *  SELECTED strategy's figure rather than whatever is typed into Manual Control. */
  sellingCosts: number;
  newHomePrice: number;
  freedCapital: number;
  buyUpShortfall: number;
  sellAndRent: boolean;
  rentPerWeek: number;
  downsizerContribution: number;
  legacyEstimate: number;
  insightFinancials: DownsizerInsightFinancials;
}

interface PanelModel {
  name?: string | null;
  before: Financials;
  after: Financials;
  projections: ProjectionYear[];
  hasDownsize: boolean;
  salePrice: number;
  /** Agent, marketing and legal costs of the sale — carried here so the plan summary quotes the
   *  SELECTED strategy's figure rather than whatever is typed into Manual Control. */
  sellingCosts: number;
  newHomePrice: number;
  freedCapital: number;
  buyUpShortfall: number;
  sellAndRent: boolean;
  rentPerWeek: number;
  downsizerContribution: number;
  legacyEstimate?: number;
  insightFinancials: DownsizerInsightFinancials | null;
}

interface DownsizerAnalyserProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  onBack: () => void;
  /** App-owned saved strategies — saving here is what makes the dashboard's Downsizing section
   * (and future SOA work) possible. Optional so the page renders without the wiring. */
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

/** The lifetime annuity an engine candidate buys, in the shape the sentence and the warnings read. */
function dzAnnuityOf(strategy: DzStrategyView, factFind: DraftFactFind): DzInBriefAnnuity | null {
  const terms = deploymentAnnuityTerms(strategy.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
  if (!terms) return null;
  const owner = terms.owner === 'partner' ? 'partner' : 'client';
  const ownerName = (owner === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you';
  return { amount: Number(terms.purchaseAmount) || 0, residualPercent: Number(terms.residualPercent) || 0, ownerName };
}

/** The age-and-health warning for a candidate's annuity — the aged care rule, applied here. */
function dzAnnuityHorizonWarningOf(strategy: DzStrategyView, factFind: DraftFactFind): string | null {
  const terms = deploymentAnnuityTerms(strategy.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
  if (!terms) return null;
  const flag = annuityHorizonFlag({ factFind: factFind as unknown as HorizonFactFind, residualCapital: (Number(terms.residualPercent) || 0) / 100, owner: terms.owner });
  return flag?.warning ?? null;
}

/** The sale-and-home facts of a candidate, read off its inputs exactly as the evaluator did. */
function dzMoveOf(strategy: DzStrategyView) {
  const cfg: DownsizeDetails = strategy.inputs || {};
  const salePrice = parseFloat(String(cfg.salePrice ?? '')) || 0;
  const sellingCosts = parseFloat(String(cfg.sellingCosts ?? '')) || 0;
  const newHomePrice = parseFloat(String(cfg.newHomePurchasePrice ?? '')) || 0;
  const stampDuty = newHomePrice > 0 ? (parseFloat(String(cfg.stampDuty ?? '')) || 0) : 0;
  const sellAndRent = cfg.rentInstead === true || (newHomePrice === 0 && salePrice > 0);
  const rentPerWeek = sellAndRent ? (Number(strategy.rentPerWeek ?? cfg.rentPerWeek) || 0) : 0;
  const netProceeds = Math.max(0, salePrice - sellingCosts);
  const freedCapital = Math.max(0, netProceeds - newHomePrice - stampDuty);
  const buyUpShortfall = sellAndRent ? 0 : Math.max(0, newHomePrice + stampDuty - netProceeds);
  const downsizerContribution = (parseFloat(String(cfg.downsizerContribution ?? '')) || 0) + (parseFloat(String(cfg.partnerDownsizerContribution ?? '')) || 0);
  return { salePrice, sellingCosts, newHomePrice, stampDuty, sellAndRent, rentPerWeek, freedCapital, buyUpShortfall, downsizerContribution };
}

const DownsizerAnalyser = ({ setActivePage, factFind, onBack, setSavedStrategies, setFactFind, onSoaGenerated }: DownsizerAnalyserProps) => {
  const isCouple = factFind?.adviceFor === 'couple';
  const hasHome = (parseFloat(String(factFind?.home?.value ?? '')) || 0) > 0;
  const homeValue = parseFloat(String(factFind?.home?.value ?? '')) || 0;
  const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
  const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
  const downsizerEligible = (clientAge != null && clientAge >= 55) || (partnerAge != null && partnerAge >= 55);
  const clientName = factFind?.client?.firstName || 'you';
  const household = isCouple && factFind?.partner?.firstName ? `${clientName} & ${factFind.partner.firstName}` : clientName;
  /** "Understanding your plan" — the plain-words recap on its own surface, as in Aged Care. */
  const [showDzExplanation, setShowDzExplanation] = useState(false);
  const [dzStrategyName, setDzStrategyName] = useState('');
  const [dzSaveNote, setDzSaveNote] = useState('');

  /*
   * THREE STEPS (owner, 24 Sep 2026: every module on the aged care spine). 1: the sale. 2: where
   * the client stands NOT downsizing — the one baseline. 3: the ways to use the money, in either
   * mode, with the rail beside them. The strip is a map, not a gate.
   */
  const [step, setStep] = useState<AnalyserStep>(1);
  const goToStep = (next: AnalyserStep) => { setStep(next); scrollAppToTop(); };

  const [dzInputs, setDzInputs] = useState<DzInputs>({
    salePrice: homeValue,
    sellingCosts: Math.round(homeValue * 0.025),
    sellAndRent: false,
    rentPerWeek: 0,
    newHomePurchasePrice: Math.round(homeValue * 0.7),
    stampDuty: 0,
    newHomeState: '',
    downsizerContribution: 0,
    partnerDownsizerContribution: 0,
    commenceIncomeStream: false,
    giftingAmount: 0,
    funeralBondPurchase: 0,
    deployment: {},
    buyUpFunding: {}, // when the new home costs more than the sale proceeds: which assets to sell to fund the gap
    buyUpCgtOverrides: {},
  });
  const [localLegacyYears, setLocalLegacyYears] = useState(10);
  // '' until the user chooses — the mode choice is the FIRST act, matching the other modules.
  const [strategyMode, setStrategyMode] = useState<'manual' | 'optimised' | ''>('');
  const [seLoading, setSeLoading] = useState(false);
  const [dzSuggestions, setDzSuggestions] = useState<DownsizerSuggestionsResult | null>(null);
  /** `id` names the engine candidate behind it, so the year toggle can re-run that one. */
  const [seDeepDive, setSeDeepDive] = useState<{ projections: ProjectionYear[]; name?: string; id?: string } | null>(null);
  // "What could go wrong" for one engine card (StrategyRisksPanel).
  const [riskStrategy, setRiskStrategy] = useState<DzStrategyView | null>(null);
  /** The card being explained — set by "Explain this", never a selection (a selection is saved). */
  const [explainSeId, setExplainSeId] = useState<string | null>(null);
  const [showStayPutProjections, setShowStayPutProjections] = useState(false);
  const [showAllSeStrategies, setShowAllSeStrategies] = useState(false);
  /** Which goal's card is shown — aged care's chips (presentation only; the engine is not re-run). */
  const [optimisationFocus, setOptimisationFocus] = useState<GoalFocus>('legacy');

  /*
   * THE DEEP DIVE'S YEAR TOGGLE (owner, 23 Sep 2026). Re-cutting a candidate on the calendar year
   * means running it again through the ENGINE — the fact-find a candidate is projected from is
   * derived server-side. The candidate, horizon and date come from the same builder that ranked it
   * (`rerun`), so nothing is reconstructed. Only offered where the candidate is known.
   */
  const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
  const [calendarYearProjections, setCalendarYearProjections] = useState<ProjectionYear[] | null>(null);
  const deepDiveCandidate = useMemo(() => {
    const rerun = dzSuggestions?.rerun;
    if (!rerun || !seDeepDive?.id) return null;
    const candidate = (rerun.candidates as { id?: unknown }[]).find((c) => c?.id === seDeepDive.id);
    return candidate ? { candidate, legacyYears: rerun.legacyYears, date: rerun.date } : null;
  }, [dzSuggestions, seDeepDive]);
  useEffect(() => {
    if (modalPeriodMode !== 'calendar_year' || !seDeepDive || !deepDiveCandidate) { setCalendarYearProjections(null); return undefined; }
    let cancelled = false;
    setCalendarYearProjections(null);
    calculationApi.evaluateDownsizerCandidates(
      factFind,
      [deepDiveCandidate.candidate],
      deepDiveCandidate.legacyYears,
      deepDiveCandidate.date,
      'calendar_year',
    ).then((rows: unknown) => {
      if (cancelled) return;
      const first = Array.isArray(rows) ? (rows[0] as { projections?: ProjectionYear[] } | undefined) : undefined;
      setCalendarYearProjections(first?.projections ?? []);
    });
    return () => { cancelled = true; };
  }, [modalPeriodMode, seDeepDive, deepDiveCandidate, factFind]);
  const [selectedSeId, setSelectedSeId] = useState<string | null>(null);
  /*
   * A real provider quote for the annuity candidates, and the draft being typed. Only the committed
   * value re-runs the field — typing a digit must not fire a run per keystroke.
   */
  const [dzAnnuityQuote, setDzAnnuityQuote] = useState<number | null>(null);
  const [dzAnnuityQuoteDraft, setDzAnnuityQuoteDraft] = useState<number | string>('');

  const salePrice = parseFloat(String(dzInputs.salePrice)) || 0;
  const sellingCosts = parseFloat(String(dzInputs.sellingCosts)) || 0;
  const newHomePrice = dzInputs.sellAndRent ? 0 : (parseFloat(String(dzInputs.newHomePurchasePrice)) || 0);
  const downsizer = parseFloat(String(dzInputs.downsizerContribution)) || 0;
  const partnerDownsizer = parseFloat(String(dzInputs.partnerDownsizerContribution)) || 0;
  const giftingAmount = parseFloat(String(dzInputs.giftingAmount)) || 0;
  const funeralBondPurchase = parseFloat(String(dzInputs.funeralBondPurchase)) || 0;
  const deployment = dzInputs.deployment; // always an object (initialised to {}, replaced by setField)
  // The duty on the new home is a cost of the changeover like the selling costs (owner, 23 Sep 2026).
  const stampDuty = !dzInputs.sellAndRent ? (parseFloat(String(dzInputs.stampDuty)) || 0) : 0;
  const netProceeds = Math.max(0, salePrice - sellingCosts - stampDuty);
  const freedCapital = Math.max(0, salePrice - sellingCosts - stampDuty - newHomePrice);
  // What's left for the client to deploy after a super top-up and any gift.
  const availableToDeploy = Math.max(0, freedCapital - downsizer - partnerDownsizer - giftingAmount - funeralBondPurchase);
  // "Buying up": the new home costs MORE than the net sale proceeds — fund the gap by selling other assets.
  const buyUpShortfall = !dzInputs.sellAndRent ? Math.max(0, newHomePrice - netProceeds) : 0;
  const buyUpFunding = dzInputs.buyUpFunding;
  const buyUpCgtOverrides = dzInputs.buyUpCgtOverrides;
  const hasDownsize = hasHome && salePrice > 0;

  const rentPerWeek = dzInputs.sellAndRent ? (parseFloat(String(dzInputs.rentPerWeek)) || 0) : 0;
  const details = useMemo<DownsizeDetails>(() => ({
    salePrice,
    sellingCosts,
    stampDuty,
    newHomeState: dzInputs.newHomeState,
    newHomePurchasePrice: newHomePrice,
    // Renting is a plan, not the absence of one: the event makes the household non-homeowner
    // renters and charges the rent (shared DownsizeDetails).
    rentInstead: dzInputs.sellAndRent,
    rentPerWeek,
    downsizerContribution: downsizer,
    partnerDownsizerContribution: partnerDownsizer,
    commenceIncomeStream: dzInputs.commenceIncomeStream,
    giftingAmount,
    funeralBondPurchase,
    deployment,
    buyUpFunding: buyUpShortfall > 0 ? buyUpFunding : undefined,
    // AssetFundingAllocator's cgtOverrides allows a `null` override (client-cleared estimate); the calc-engine
    // details bag only expects a numeric override, so a null here simply carries no override through.
    buyUpCgtOverrides: buyUpCgtOverrides as Record<string, number>,
  }), [salePrice, sellingCosts, stampDuty, dzInputs.newHomeState, newHomePrice, dzInputs.sellAndRent, rentPerWeek, downsizer, partnerDownsizer, dzInputs.commenceIncomeStream, giftingAmount, funeralBondPurchase, deployment, buyUpShortfall, buyUpFunding, buyUpCgtOverrides]);

  /*
   * TODAY'S POSITION — the standalone assessment of the profile as it stands, run always: it is
   * step 2's four numbers, the rail's "before", and what the engine's rail holds.
   */
  const [todayFinancials, setTodayFinancials] = useState<Financials | null>(null);
  useEffect(() => {
    let cancelled = false;
    calculationApi.calculateFinancials(factFind).then((r) => { if (!cancelled) setTodayFinancials(r as Financials); }).catch(() => { if (!cancelled) setTodayFinancials(null); });
    return () => { cancelled = true; };
  }, [factFind]);

  const [{ before, after: afterRaw }, setBeforeAfter] = useState<{ before: Financials; after: Financials }>({ before: {}, after: {} });
  useEffect(() => {
    let cancelled = false;
    if (!hasDownsize) { setBeforeAfter({ before: {}, after: {} }); return undefined; }
    getDownsizeBeforeAfter(factFind, details).then((r) => { if (!cancelled) setBeforeAfter(r); });
    return () => { cancelled = true; };
  }, [factFind, details, hasDownsize]);

  /**
   * When the downsize happens, blank = model it from today. With a future date the engine performs
   * the sale, purchase and allocations on the day, against the balances as they stand then — the
   * years before it are the client's current life, unchanged.
   */
  const [downsizeDate, setDownsizeDate] = useState<string>('');
  const downsizeDateInPast = downsizeDate !== '' && downsizeDate < new Date().toISOString().slice(0, 10);
  const effectiveDownsizeDate = downsizeDateInPast ? '' : downsizeDate;

  /** The shared debt strategy — see the Aged Care module. Applied at origin, like the downsize itself (or on the chosen date, when one is set). */
  const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
  const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
  const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
  const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
    const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding, repaymentBasis: debtRepaymentBasis };
  }, [debtPayments, debtFunding, debtRepaymentBasis]);
  // The after-position with the payoff in it — recomputed only when a plan exists, so the no-debt
  // path stays exactly the round-trip it always was.
  const [afterWithDebt, setAfterWithDebt] = useState<Financials | null>(null);
  useEffect(() => {
    if (!debtPayoffPlan || !hasDownsize) { setAfterWithDebt(null); return undefined; }
    let cancelled = false;
    (async () => {
      const { simulated } = await getDownsizeBeforeAfter(factFind, details);
      const withDebt = applyDebtPayoffToFactFind(simulated as never, debtPayoffPlan).factFind;
      const r = (await calculationApi.calculateFinancials(withDebt)) as Financials;
      if (!cancelled) setAfterWithDebt(r);
    })();
    return () => { cancelled = true; };
  }, [factFind, details, debtPayoffPlan, hasDownsize]);

  const after = afterWithDebt ?? afterRaw;

  const [projections, setProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!hasDownsize) { setProjections([]); return undefined; }
    (async () => {
      try {
        let proj: ProjectionYear[];
        if (effectiveDownsizeDate) {
            // Dated: the ORIGINAL fact-find goes to the engine, which executes the downsize on the
            // day against live balances. The debt payoff rides the same options (engine pays it at the
            // start — a lender takes the money any day). Pre-applying either here as well would run
            // them twice.
            proj = await runDownsizeProjections(factFind, {
                legacyYears: localLegacyYears,
                downsizeDate: effectiveDownsizeDate,
                downsizeDetails: details as never,
                debtPayoff: debtPayoffPlan as never,
            });
        } else {
            const { simulated } = await getDownsizeBeforeAfter(factFind, details);
            const modelled = debtPayoffPlan ? (applyDebtPayoffToFactFind(simulated as never, debtPayoffPlan).factFind as never) : simulated;
            proj = await runDownsizeProjections(modelled, { legacyYears: localLegacyYears });
        }
        if (!cancelled) setProjections(proj);
      } catch { if (!cancelled) setProjections([]); }
    })();
    return () => { cancelled = true; };
  }, [factFind, details, hasDownsize, localLegacyYears, debtPayoffPlan, effectiveDownsizeDate]);
  const legacyEstimate = projections.length > 0 ? (projections[localLegacyYears - 1]?.netWorth ?? projections[projections.length - 1]?.netWorth ?? after?.netWorth) : after?.netWorth;

  const insightFinancials = useMemo<DownsizerInsightFinancials | null>(() => (hasDownsize ? {
    agePensionBefore: before?.socialSecurity ?? 0,
    agePensionAfter: after?.socialSecurity ?? 0,
    cashflowAfter: projections[0]?.netCashflow ?? after?.cashFlow ?? 0,
    legacyEstimate,
  } : null), [hasDownsize, before, after, projections, legacyEstimate]);

  /*
   * THE BENCHMARK: this client's position over the horizon if they simply do not downsize. Kept
   * WHOLE, not just its last row: the stay-put projection is the position step 2 shows, the runway
   * the rail carries until a plan is named, and what "See the projections" opens in that state —
   * one run, read everywhere not downsizing is quoted. The same run the engine's own stay-put
   * candidate makes.
   */
  const [stayPutProjections, setStayPutProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    let cancelled = false;
    // The module's own projection helper with NO downsize details: the client's life as it is,
    // over the horizon — the same run the engine's stay-put candidate makes.
    runDownsizeProjections(factFind, { legacyYears: localLegacyYears })
      .then((rows) => { if (!cancelled) setStayPutProjections(rows || []); })
      .catch(() => { if (!cancelled) setStayPutProjections([]); });
    return () => { cancelled = true; };
  }, [factFind, localLegacyYears]);
  const manualBaselineLegacy: number | null = useMemo(() => {
    const last = stayPutProjections[localLegacyYears - 1] ?? stayPutProjections[stayPutProjections.length - 1];
    return typeof last?.netWorth === 'number' ? last.netWorth : null;
  }, [stayPutProjections, localLegacyYears]);
  const stayPutRunway = useMemo(
    () => (stayPutProjections.length ? planRunwayCopy(stayPutProjections as never, Number(localLegacyYears) || 5) : null),
    [stayPutProjections, localLegacyYears],
  );

  /*
   * THE STRATEGY ANALYSIS SETUP (owner, 2 Oct 2026) — buy or rent, the new home's price and
   * state, the rent, the annuity share. Seeded from the client record, with Manual Control's
   * figures as the fallback; opened the first time the engine is chosen; "Adjust setup" reopens.
   */
  const [dzSetup, setDzSetup] = useState<DzEngineSetup>(() => dzEngineSetupFromGoals(factFind, {
    housing: dzInputs.sellAndRent ? 'rent' : 'buy',
    newHomePrice: dzInputs.newHomePurchasePrice,
    newHomeState: dzInputs.newHomeState,
    rentPerWeek: dzInputs.rentPerWeek,
  }));
  const [dzSetupOpen, setDzSetupOpen] = useState(false);
  const [dzSetupSeen, setDzSetupSeen] = useState(false);

  // The just-in-time "how would new money be invested?" gate — every downsizer SE run deploys
  // freed-up money (shares, super, annuity), so the only conditions are "unanswered" and "not
  // skipped this visit". Asked in the setup pop-over first; this is the fallback when that was
  // cancelled. Answering saves to the profile (asked once, ever).
  const [newMoneySkipped, setNewMoneySkipped] = useState(false);
  const needsNewMoneyAnswer = strategyMode === 'optimised' && hasDownsize && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped;
  const answerNewMoneyProfile = (id: InvestmentProfileId) => {
    if (setFactFind) {
      setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
    } else {
      setNewMoneySkipped(true);
    }
  };

  /*
   * THE ANNUITY WILLINGNESS QUESTION (owner, 25 Sep 2026), asked in the setup and, where that was
   * cancelled, here. "No" removes every annuity strategy from this and every other module — the
   * answer is the client's, not this screen's (annuityPreference.ts).
   */
  const [annuityPrefSkipped, setAnnuityPrefSkipped] = useState(false);
  const needsAnnuityAnswer = !annuityPreferenceOf(factFind as never).answered && !annuityPrefSkipped;
  const answerAnnuityPreference = (appetite: AnnuityAppetite, residual: AnnuityResidual) => {
    if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), annuityAppetite: appetite, annuityResidual: residual } }));
    else setAnnuityPrefSkipped(true);
    setDzSetup((prev) => ({ ...prev, annuityAppetite: appetite, annuityResidual: residual }));
  };

  // The Transfer Balance Cap question: only when this run would COMMENCE a pension and a person's
  // balances trip the $1.6m screen (transferBalanceCap.ts). Skipping estimates cap usage from
  // current pension balances — stated in the advice document.
  const [tbcSkipped, setTbcSkipped] = useState(false);
  const seRunCommencesPension = useMemo(() => {
    if (!hasDownsize) return false;
    try {
      return generateDownsizerCandidates(factFind, { currentInputs: details, setup: dzSetup }).some((c) => {
        const d = c.details as { commenceIncomeStream?: boolean; deployment?: { superCommenceIncomeStream?: boolean } };
        return d.commenceIncomeStream === true || d.deployment?.superCommenceIncomeStream === true;
      });
    } catch { return false; }
  }, [factFind, details, dzSetup, hasDownsize]);
  const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && seRunCommencesPension
    ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
      : (isCouple && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
    : null;
  const needsTbcAnswer = strategyMode === 'optimised' && tbcPersonNeedingAnswer !== null;
  const answerTbc = (amount: number) => {
    if (setFactFind && tbcPersonNeedingAnswer) {
      const key = tbcPersonNeedingAnswer;
      setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
    } else {
      setTbcSkipped(true);
    }
  };

  // Suggestion Engine: paint loader, then compute, then hold to a minimum (shared pattern). The
  // engine waits while the setup is open — its answers are inputs to the run.
  useEffect(() => {
    if (strategyMode !== 'optimised') return undefined;
    if (dzSetupOpen) { setSeLoading(false); return undefined; }
    if (needsNewMoneyAnswer || needsTbcAnswer) { setSeLoading(false); return undefined; }
    if (!hasDownsize) { setDzSuggestions(null); setSeLoading(false); return undefined; }
    setSeLoading(true);
    const startedAt = Date.now();
    const t = setTimeout(async () => {
      let result: DownsizerSuggestionsResult | null = null;
      try { result = await buildDownsizerSuggestions(factFind, { legacyYears: localLegacyYears, currentInputs: details, downsizeDate: effectiveDownsizeDate || undefined, setup: dzSetup, annuityIncomeOverride: dzAnnuityQuote }); } catch { result = null; }
      setDzSuggestions(result);
      setTimeout(() => setSeLoading(false), Math.max(0, 2000 - (Date.now() - startedAt)));
    }, 60);
    return () => clearTimeout(t);
  // dzAnnuityQuote: a committed provider quote re-ranks the whole field, because a different rate
  // can change which strategy wins.
  }, [strategyMode, factFind, localLegacyYears, details, hasDownsize, needsNewMoneyAnswer, needsTbcAnswer, dzSetup, dzSetupOpen, effectiveDownsizeDate, dzAnnuityQuote]);

  const dzStrategies = useMemo(
    () => (dzSuggestions ? enrichStrategiesWithRecommendationLabels(dzSuggestions.strategies, dzSuggestions.recommended) : []) as DzStrategyView[],
    [dzSuggestions],
  );
  const dzStayPut = useMemo(() => (dzSuggestions?.stayPut as DzStrategyView | null | undefined) ?? null, [dzSuggestions]);
  const isDzBaseline = (s: DzStrategyView) => s.id === 'dz-stay-put';

  // Nothing is selected for the reader (aged care's rule): a card is selected by choosing it. Cleared outside engine mode.
  useEffect(() => {
    if (strategyMode !== 'optimised' && selectedSeId !== null) setSelectedSeId(null);
  }, [strategyMode, selectedSeId]);

  /*
   * GOAL LEADERS, with the aged care horizon rule (owner, 23 Sep 2026: "a flagged strategy should
   * not win a ranking"). A candidate whose annuity needs more years than this client's age and
   * health point to is set aside from the leadership — never hidden: the note below the cards
   * names it and opens it.
   */
  const dzRecommended = useMemo<RecommendedStrategyEntry[]>(() => {
    if (!dzSuggestions) return [];
    const movers = dzStrategies.filter((s) => !isDzBaseline(s) && !isDzSensitivity(s));
    const eligible = movers.filter((s) => dzAnnuityHorizonWarningOf(s, factFind) == null);
    if (eligible.length === movers.length) return dzSuggestions.recommended;
    return buildRecommendedOptimalStrategies(eligible, dzStayPut, { cashflowTarget: Number(dzSetup.cashflowTarget) || 0 });
  }, [dzSuggestions, dzStrategies, dzStayPut, factFind, dzSetup.cashflowTarget]);
  const dzGoalLeaders: DzStrategyView[] = useMemo(() => mergeGoalLeaderStrategies(dzRecommended) as DzStrategyView[], [dzRecommended]);
  /** Whether the cashflow-covered leader is also the best estate outright — said, not shown twice. */
  const coveredSameAsEstate = useMemo(() => coveredIsBestEstate(dzRecommended), [dzRecommended]);
  /** The cashflow-covered leader's case, for the line on its card. */
  const coveredNoteFor = (s: DzStrategyView): string | null => {
    const v = dzRecommended.find((r) => r.label === CASHFLOW_COVERED_LABEL && r.strategy?.name === s.name)?.cashflowCovered;
    return v ? cashflowCoveredNote(v, Number(localLegacyYears) || 5, formatCurrency) : null;
  };
  /** The leaders the horizon rule set aside, so the screen can say so and open them. */
  const dzSetAside = useMemo(() => {
    if (!dzSuggestions) return [] as Array<{ strategy: DzStrategyView; labels: string[]; warning: string }>;
    return (mergeGoalLeaderStrategies(dzSuggestions.recommended) as DzStrategyView[])
      .map((s) => ({ strategy: s, labels: s.recommendationLabels || [], warning: dzAnnuityHorizonWarningOf(s, factFind) || '' }))
      .filter((x) => x.warning);
  }, [dzSuggestions, factFind]);
  const explainStrategy: DzStrategyView | null = useMemo(
    () => (strategyMode === 'optimised' ? (dzStrategies.find((s) => s.id === explainSeId) || null) : null),
    [strategyMode, dzStrategies, explainSeId],
  );
  /** Which strategy CARDS appear, and what each is badged as — the shared rule (engineGoalCards.ts). */
  const seCards = useMemo(() => buildGoalCards<DzStrategyView>({
    strategies: dzStrategies,
    goalLeaders: dzGoalLeaders,
    baseline: dzStayPut,
    isBaseline: isDzBaseline,
    isSensitivity: (s) => isDzSensitivity(s),
    selectedId: selectedSeId,
    baselineWord: 'not downsizing',
    formatCurrency,
  }), [dzStrategies, dzGoalLeaders, dzStayPut, selectedSeId]);
  const visibleSeCards = useMemo(() => visibleGoalCards(seCards, optimisationFocus, coveredSameAsEstate), [seCards, optimisationFocus, coveredSameAsEstate]);
  const labelMap: Record<string, string> = { ...GOAL_CHIP_LABELS };

  /**
   * "Select Strategy" on an engine card SAVES it, exactly as it does in Aged Care — the saved list
   * is what the Dashboard and the advice document read. Recorded with its source and what the
   * engine recommended.
   */
  const selectEngineStrategy = (strategy: DzStrategyView) => {
    setSelectedSeId(strategy.id ?? null);
    if (!setSavedStrategies) return;
    const recommended = dzGoalLeaders.map((l) => ({ labels: l.recommendationLabels || [], name: l.name }));
    setSavedStrategies((prev) => [
      ...(prev || []),
      {
        id: `dz-se-${Date.now()}`,
        name: strategy.name || 'Suggested downsizing strategy',
        type: 'downsizer',
        source: 'suggestion_engine',
        inputs: {
          details: { ...(strategy.inputs || {}) },
          downsizeDate: effectiveDownsizeDate || null,
          setup: { ...dzSetup },
          source: 'suggestion_engine',
          seStrategyName: strategy.name ?? null,
          recommendedOptimalStrategies: recommended,
          clientSelectedIsAlternative: recommended.length > 0 && !recommended.some((r) => r.name === strategy.name),
        },
        outputs: {
          agePension: strategy.snapshot?.pension ?? 0,
          cashFlow: strategy.snapshot?.netCashflow ?? 0,
          legacyEstimate: strategy.snapshot?.legacyValue ?? 0,
          netWorthSchedule: buildNetWorthSchedule((strategy.projections || []) as never),
        },
        createdAt: new Date().toISOString(),
      },
    ]);
  };

  /**
   * The Statement of Advice for the selected engine strategy (dzSoa.ts) — opened, downloaded,
   * registered. On the app's calendar-year setting the chosen plan and the benchmark are re-cut on
   * calendar years first (the deep dive's own run), so the document's rows match the screen
   * (owner, 3 Oct 2026). If that run fails the financial-year rows stand.
   */
  const generateDzSoa = async (strategy: DzStrategyView) => {
    const periodMode = readPeriodMode();
    let picked = strategy;
    let all = dzStrategies;
    const rerun = dzSuggestions?.rerun;
    if (periodMode === 'calendar_year' && rerun) {
      const wanted = (rerun.candidates as { id?: unknown }[]).filter((c) => c?.id === strategy.id || c?.id === 'dz-stay-put');
      if (wanted.length) {
        try {
          const rows = (await calculationApi.evaluateDownsizerCandidates(factFind, wanted, rerun.legacyYears, rerun.date, 'calendar_year')) as Array<{ id?: string; projections?: ProjectionYear[] }>;
          const byId = new Map(rows.map((r) => [String(r.id), r.projections || []]));
          picked = { ...strategy, projections: byId.get(String(strategy.id)) ?? strategy.projections };
          all = dzStrategies.map((s) => (byId.has(String(s.id)) ? { ...s, projections: byId.get(String(s.id)) } : s));
        } catch { /* the financial-year rows stand */ }
      }
    }
    const snapshot = buildDownsizerSoaSnapshot({ strategy: picked, all, factFind, legacyYears: Number(localLegacyYears) || 5, setup: dzSetup, periodMode });
    openModuleSoa(snapshot);
    onSoaGenerated?.({ id: strategy.id, name: strategy.name, type: 'downsizer', source: 'suggestion_engine', strategy, soaSnapshot: snapshot });
  };

  // The explanation of an engine card is built from ITS before/after, never from Manual Control's
  // inputs (which would re-trigger the engine) and never from the rail, which holds not downsizing.
  const [explainView, setExplainView] = useState<SeView | null>(null);
  useEffect(() => {
    if (!explainStrategy) { setExplainView(null); return undefined; }
    let cancelled = false;
    const cfg: DownsizeDetails = explainStrategy.inputs || {};
    getDownsizeBeforeAfter(factFind, cfg).then(({ before: sBefore, after: sAfter }) => {
      if (cancelled) return;
      const proj = explainStrategy.projections || [];
      const m = dzMoveOf(explainStrategy);
      const sLegacy = proj.length
        ? (proj[localLegacyYears - 1]?.netWorth ?? proj[proj.length - 1]?.netWorth ?? sAfter?.netWorth ?? 0)
        : (sAfter?.netWorth ?? 0);
      setExplainView({
        name: explainStrategy.name,
        before: sBefore, after: sAfter, projections: proj,
        hasDownsize: m.salePrice > 0, salePrice: m.salePrice, sellingCosts: m.sellingCosts, newHomePrice: m.newHomePrice, freedCapital: m.freedCapital,
        buyUpShortfall: m.buyUpShortfall, sellAndRent: m.sellAndRent, rentPerWeek: m.rentPerWeek,
        downsizerContribution: m.downsizerContribution,
        legacyEstimate: sLegacy,
        insightFinancials: {
          agePensionBefore: sBefore?.socialSecurity ?? 0,
          agePensionAfter: sAfter?.socialSecurity ?? 0,
          cashflowAfter: proj[0]?.netCashflow ?? proj[0]?.cashFlow ?? sAfter?.cashFlow ?? 0,
          legacyEstimate: sLegacy,
        },
      });
    });
    return () => { cancelled = true; };
  }, [explainStrategy, factFind, localLegacyYears]);

  // Manual Control's model: the narrative, the rail and the explanation read one object.
  const panelModel = useMemo<PanelModel>(() => ({
    name: null,
    before, after, projections,
    hasDownsize, salePrice, sellingCosts, newHomePrice, freedCapital, buyUpShortfall,
    sellAndRent: dzInputs.sellAndRent, rentPerWeek, downsizerContribution: downsizer + partnerDownsizer,
    legacyEstimate, insightFinancials,
  }), [before, after, projections, hasDownsize, salePrice, sellingCosts, newHomePrice, freedCapital, buyUpShortfall, dzInputs.sellAndRent, rentPerWeek, downsizer, partnerDownsizer, legacyEstimate, insightFinancials]);

  const setField = <K extends keyof DzInputs>(field: K, value: DzInputs[K]) => setDzInputs((prev) => ({ ...prev, [field]: value }));

  /*
   * ── THE PLAN SUMMARY RAIL ────────────────────────────────────────────────────────────────────
   * The same single panel as Aged Care, Retirement Village and Support at Home. THE RAIL HOLDS
   * NOT DOWNSIZING — the one baseline — until Manual Control names a plan; in engine mode always:
   * each card carries its own figures, measured against this rail (the aged care pattern).
   */
  const dzPlanSummaryName = strategyMode === 'manual' && panelModel.hasDownsize ? 'Your downsizing plan' : null;
  const railHoldsStayPut = !dzPlanSummaryName;
  const railProjections: ProjectionYear[] = railHoldsStayPut ? stayPutProjections : panelModel.projections;
  const dzRunway = useMemo(
    () => (railProjections.length ? planRunwayCopy(railProjections as never, Number(localLegacyYears) || 5) : null),
    [railProjections, localLegacyYears],
  );
  const railBefore = (todayFinancials ?? before) as FinancialPosition;

  /** The panel's "In brief": the SAME sentence template every engine card carries (dzInBrief.tsx). */
  const dzInBrief = useMemo<React.ReactNode | null>(() => {
    if (!dzPlanSummaryName) return null;
    const cashBefore = panelModel.before?.cashFlow ?? 0;
    const manualTerms = deploymentAnnuityTerms(deployment, isCouple);
    return (
      <DzInBrief
        salePrice={panelModel.salePrice}
        freedCapital={panelModel.freedCapital}
        buyUpShortfall={panelModel.buyUpShortfall}
        sellAndRent={panelModel.sellAndRent}
        rentPerWeek={panelModel.rentPerWeek}
        downsizerContribution={panelModel.downsizerContribution}
        pensionBefore={panelModel.before?.socialSecurity ?? 0}
        pensionAfter={panelModel.after?.socialSecurity ?? 0}
        cashBefore={cashBefore}
        cashAfter={panelModel.projections[0]?.netCashflow ?? panelModel.after?.cashFlow ?? cashBefore}
        planLegacy={panelModel.legacyEstimate ?? null}
        baseLegacy={manualBaselineLegacy}
        legacyYears={Number(localLegacyYears) || 0}
        annuity={manualTerms ? { amount: Number(manualTerms.purchaseAmount) || 0, residualPercent: Number(manualTerms.residualPercent) || 0, ownerName: (manualTerms.owner === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you' } : null}
      />
    );
  }, [dzPlanSummaryName, panelModel, manualBaselineLegacy, localLegacyYears, deployment, isCouple, factFind]);

  /** The plain-words recap, read inside "Understanding your plan" rather than inline. */
  const recapModel = explainView ?? panelModel;
  const dzRecapNode = (
    <DownsizerPlanRecap
      model={recapModel}
      giftingAmount={explainView ? (parseFloat(String(explainStrategy?.inputs?.giftingAmount ?? '')) || 0) : giftingAmount}
      firstName={factFind?.client?.firstName}
      baselineLegacy={manualBaselineLegacy}
      legacyYears={localLegacyYears}
    />
  );

  /** The engine card's money flow: home sold → new home or rent → a nil-residual annuity. */
  const flowFor = (s: DzStrategyView): MoneyFlowStep[] => {
    const m = dzMoveOf(s);
    const annuity = dzAnnuityOf(s, factFind);
    const steps: MoneyFlowStep[] = [{ amount: m.salePrice, label: 'home sold', sub: `less ${formatCurrency(m.sellingCosts)} selling costs`, tone: 'sold' }];
    if (m.sellAndRent) steps.push({ amount: m.rentPerWeek * 52, label: 'rent a year', sub: 'a cost for life · Rent Assistance may help', tone: 'warn' });
    else if (m.newHomePrice > 0) steps.push({ amount: m.newHomePrice, label: 'new home', sub: m.stampDuty > 0 ? `exempt · plus ${formatCurrency(m.stampDuty)} duty` : 'exempt from the assets test', tone: 'good' });
    if (annuity && Math.round(annuity.residualPercent) === 0) steps.push({ amount: annuity.amount, label: 'lifetime annuity', sub: 'nothing returned on death', tone: 'warn' });
    return steps;
  };

  const stepDefs = [
    { title: 'Your home sale', sub: 'The sale price, the costs and the date' },
    { title: `Where ${clientName} ${factFind?.client?.firstName ? 'stands' : 'stand'} today`, sub: 'Four numbers, not downsizing' },
    { title: 'Ways to use the money', sub: 'Recommended plans, or build your own' },
  ] as const;

  return (
    <div className="max-w-[1520px] mx-auto">

      <div className="flex items-center gap-2 mb-4 -ml-2">
        <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack}>Back</Button>
        <button onClick={() => setActivePage('Dashboard')} className="text-blue-600 hover:text-blue-800 font-medium text-sm">Back to Dashboard</button>
      </div>
      <h1 className="text-4xl font-bold text-gray-900 mb-2"><GradientText>Downsizing Calculator</GradientText></h1>
      <p className="text-lg text-slate-700 leading-relaxed">
        Three short steps: the sale, where {household} {isCouple && factFind?.partner?.firstName ? 'stand' : (factFind?.client?.firstName ? 'stands' : 'stand')} today without it, and the ways to use the money.
      </p>

      {!hasHome ? (
        <Card className="mt-6"><p className="text-sm text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-3">Downsizing applies when you own a home. There&apos;s no home recorded in your profile, so there&apos;s nothing to downsize here.</p></Card>
      ) : (
      <>
      <AnalyserSteps step={step} onStep={goToStep} steps={stepDefs} ariaLabel="Downsizing analyser steps" />

      {step === 1 && (
        <div className="mt-8 space-y-6">
          {/* THE CASE FIRST, the modelling modes second. The date and the sale figures are facts
              about this client's downsize — both modes consume them (the Suggestion Engine's
              candidates are generated from this sale price and evaluated on this date). */}
          <Card>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Your home sale</h3>
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-end gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
                <div className="flex-1">
                  <label htmlFor="dz-event-date" className="block text-sm font-semibold text-gray-800">When are you thinking of downsizing?</label>
                  <p className="text-xs text-gray-600 mt-1">
                    Leave this blank to model it from today — the right choice if you&apos;re still deciding.
                    Choose a future date and we&apos;ll show life as it is now until then, with the move
                    happening on the day you pick.
                  </p>
                </div>
                <div>
                  <input
                    id="dz-event-date"
                    type="date"
                    value={downsizeDate}
                    onChange={(e) => setDownsizeDate(e.target.value)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                  {downsizeDateInPast && (
                    <p className="text-xs text-amber-700 mt-1">A past date models as &ldquo;downsizing now&rdquo;.</p>
                  )}
                </div>
              </div>
              {/* Only when a date is set, because that is the only time there is a gap to explain. */}
              <ProceedsExemptionNote downsizeDate={downsizeDate} freedCapital={freedCapital} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CurrencyInput label="Sale price of your home" value={dzInputs.salePrice ?? ''} onChange={(v) => setField('salePrice', v ?? 0)} className="" />
                <CurrencyInput label="Selling costs (agent, legal)" value={dzInputs.sellingCosts ?? ''} onChange={(v) => setField('sellingCosts', v ?? 0)} className="" />
              </div>
            </div>
          </Card>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => goToStep(2)}
              disabled={!(salePrice > 0)}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              See where {clientName} {factFind?.client?.firstName ? 'stands' : 'stand'} today <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <DzStayPutHero
            factFind={factFind}
            todayFinancials={todayFinancials as { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null}
            stayPutLegacy={manualBaselineLegacy}
            runway={stayPutRunway}
            legacyYears={localLegacyYears}
            salePrice={salePrice}
            sellingCosts={sellingCosts}
            onLegacyYearsChange={setLocalLegacyYears}
            onBack={() => goToStep(1)}
            onNext={() => goToStep(3)}
            onAssumptions={() => setActivePage('AssumptionsPage')}
            onShowProjections={() => setShowStayPutProjections(true)}
          />
        </div>
      )}

      {step === 3 && (
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
        <div className="xl:col-span-3 space-y-6">
          <Card>
            <ModeChooser
              heading="How do you want to model your options?"
              value={strategyMode}
              onSelect={(id) => {
                const next = id as 'manual' | 'optimised';
                setStrategyMode(next);
                // The setup opens once per visit on choosing the engine; "Adjust setup" reopens it.
                if (next === 'optimised' && !dzSetupSeen) setDzSetupOpen(true);
              }}
              options={[
                {
                  id: 'manual',
                  title: 'Manual Control',
                  blurb: 'Choose the new home price — or renting instead — and where every freed-up dollar goes: super, gifts, investments.',
                  chip: 'Advisers & confident users',
                },
                {
                  id: 'optimised',
                  title: 'Suggestion Engine',
                  blurb: 'A few choices from you — buy or rent, the price, how much could be locked away — then it models every way to use the money and shows the strongest for each goal.',
                  chip: 'Most people start here',
                },
              ]}
            />
          </Card>
          <DzAnalysisSetupModal
            isOpen={dzSetupOpen}
            onClose={() => { setDzSetupOpen(false); setDzSetupSeen(true); }}
            factFind={factFind}
            initial={dzSetup}
            salePrice={salePrice}
            sellingCosts={sellingCosts}
            askNewMoney={!hasAnsweredNewMoneyProfile(factFind)}
            tbcPerson={tbcPersonNeedingAnswer}
            onContinue={(next, extras) => {
              setDzSetup(next);
              setDzSetupSeen(true);
              setDzSetupOpen(false);
              if (extras.newMoneyProfile) answerNewMoneyProfile(extras.newMoneyProfile); else setNewMoneySkipped(true);
              if (tbcPersonNeedingAnswer) { if (extras.tbcAmount != null) answerTbc(extras.tbcAmount); else setTbcSkipped(true); }
              // The annuity answer is the client's, read by every module.
              if (next.annuityAppetite === 'none' || next.annuityAppetite === 'yes') setAnnuityPrefSkipped(false);
              if (setFactFind) {
                setFactFind((prev) => ({
                  ...prev,
                  goals: {
                    ...(prev.goals || {}),
                    dzMotive: next.motive ?? 'both',
                    dzHousing: next.housing ?? 'buy',
                    dzNewHomePrice: Number(next.newHomePrice) || 0,
                    dzNewHomeState: next.newHomeState || '',
                    dzRentPerWeek: Number(next.rentPerWeek) || 0,
                    dzAnnuityShareCapPct: Number(next.annuityShareCapPct),
                    annuityAppetite: next.annuityAppetite ?? 'unsure',
                    annuityResidual: next.annuityResidual ?? 'both',
                  },
                }));
              } else {
                setAnnuityPrefSkipped(true);
              }
            }}
          />

          {strategyMode === 'manual' && (<>
          <Card>
            <h3 className="text-xl font-bold text-gray-800 mb-4">What happens with the money</h3>
            <div className="space-y-4">

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                <input type="checkbox" checked={dzInputs.sellAndRent} onChange={(e) => setField('sellAndRent', e.target.checked)} className="mt-1 rounded" />
                <span>
                  <span className="block font-medium text-gray-800">Sell and rent instead of buying</span>
                  <span className="block text-sm text-gray-500">You become a non-homeowner (may get Rent Assistance, but all your equity is then assessable).</span>
                </span>
              </label>
              {dzInputs.sellAndRent && (
                <>
                  <SellAndRentNote salePrice={salePrice} isCouple={factFind?.adviceFor === 'couple'} />
                  {/* The rent is a cost of the plan, charged for life and indexed, and the figure
                      Rent Assistance is assessed on. Without it the figures assume none, and say so. */}
                  <div className="max-w-xs">
                    <CurrencyInput label="Rent you would pay (a week)" value={dzInputs.rentPerWeek || ''} onChange={(v) => setField('rentPerWeek', v ?? 0)} className="" />
                  </div>
                  {!(rentPerWeek > 0) && (
                    <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-2">
                      No rent entered, so the figures below charge none and pay no Rent Assistance. Enter the weekly rent for an honest comparison.
                    </p>
                  )}
                </>
              )}

              {!dzInputs.sellAndRent && (
                <>
                  <CurrencyInput label="Price of your new home" value={dzInputs.newHomePurchasePrice ?? ''} onChange={(v) => setField('newHomePurchasePrice', v ?? 0)} className="" />
                  <StampDutyFields
                    state={dzInputs.newHomeState}
                    onStateChange={(v) => setField('newHomeState', v)}
                    newHomePrice={dzInputs.newHomePurchasePrice}
                    duty={dzInputs.stampDuty}
                    onDutyChange={(v) => setField('stampDuty', v)}
                  />
                </>
              )}

              {buyUpShortfall > 0 ? (
                <>
                  <div className="rounded-lg border border-slate-200 border-l-4 border-l-amber-500 bg-slate-50 p-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800">Extra needed for the new home <InfoIcon text="Your new home costs more than your sale proceeds. A dearer home is still an exempt asset, so this can lift your Age Pension — but you fund the gap by selling other assets (the trade-off)." /></span>
                    <span className="text-lg font-bold text-amber-700">{formatCurrency(buyUpShortfall)}</span>
                  </div>
                  <AssetFundingAllocator
                    factFind={factFind}
                    target={buyUpShortfall}
                    funding={buyUpFunding}
                    onChange={(m: Record<string, number>) => setField('buyUpFunding', m)}
                    cgtOverrides={buyUpCgtOverrides}
                    onCgtOverridesChange={(o: Record<string, number | null>) => setField('buyUpCgtOverrides', o)}
                    targetLabel="Extra needed"
                    help="Your new home costs more than your sale proceeds. Choose which assets to sell to fund the difference. Moving money into a (larger) exempt home can increase your Age Pension — the trade-off is the income those assets were earning."
                  />
                </>
              ) : (
                <>
                  <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800">Money freed up</span>
                    <span className="text-lg font-bold text-blue-700">{formatCurrency(freedCapital)}</span>
                  </div>
                  {/* Names the trap where the money that causes it first appears, and points at the
                      two boxes below that soften it — which otherwise read as just more options. */}
                  <DownsizingTrapNote
                    pensionBefore={panelModel.before?.socialSecurity ?? 0}
                    pensionAfter={panelModel.after?.socialSecurity ?? 0}
                    freedCapital={freedCapital}
                    alreadySoftened={giftingAmount > 0 || funeralBondPurchase > 0}
                  />

                  {/* A downsizer contribution needs a qualifying SALE, never a purchase — a client
                      who sells and rents may still contribute (ATO; owner-caught 2 Oct 2026). */}
                  {downsizerEligible && (
                    <div className="space-y-2">
                      <CurrencyInput label={<span>Move to super — downsizer contribution {isCouple ? `(${factFind?.client?.firstName || 'you'})` : ''} <InfoIcon text="Up to $300,000 per person (55+). Doesn't count to your contribution caps; must be within 90 days of settlement. You do not have to buy another home." /></span>} value={dzInputs.downsizerContribution ?? ''} onChange={(v) => setField('downsizerContribution', Math.min(v ?? 0, DOWNSIZER_CONTRIBUTION_CAP))} className="" />
                      {isCouple && (
                        <CurrencyInput label={<span>Move to super — downsizer ({factFind?.partner?.firstName || 'partner'})</span>} value={dzInputs.partnerDownsizerContribution ?? ''} onChange={(v) => setField('partnerDownsizerContribution', Math.min(v ?? 0, DOWNSIZER_CONTRIBUTION_CAP))} className="" />
                      )}
                      {/* What the contribution buys AND what it costs — the pension side is the
                          half a client is most likely to miss, and it was in the floating panel. */}
                      <DownsizerContributionNote
                        clientAmount={downsizer}
                        partnerAmount={partnerDownsizer}
                      />
                      <DownsizerRulesAndInsights factFind={factFind} />
                    </div>
                  )}

                  <CurrencyInput label={<span>Gift to family from the proceeds (if any) <InfoIcon text="Gifts above $10,000/year or $30,000 over 5 years still count for the pension for 5 years." /></span>} value={dzInputs.giftingAmount ?? ''} onChange={(v) => setField('giftingAmount', v ?? 0)} className="" />
                  {/* THE CHEAPEST FIX FOR THE DOWNSIZING TRAP: the home was exempt and the money it
                      becomes is not; a funeral bond puts part of it back outside the assets test. */}
                  <CurrencyInput
                    label={<span>Funeral bond from the proceeds (if any) <InfoIcon text={`Exempt from the assets test up to ${formatCurrency(FUNERAL_BOND_LIMIT)} per person. Above that, the excess is counted like any other asset.`} /></span>}
                    value={dzInputs.funeralBondPurchase ?? ''}
                    onChange={(v) => setField('funeralBondPurchase', v ?? 0)}
                    className=""
                  />
                  {funeralBondPurchase > FUNERAL_BOND_LIMIT * (factFind?.adviceFor === 'couple' ? 2 : 1) && (
                    <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-2">
                      Only {formatCurrency(FUNERAL_BOND_LIMIT * (factFind?.adviceFor === 'couple' ? 2 : 1))} of this is
                      exempt{factFind?.adviceFor === 'couple' ? ' (one bond each)' : ''}. The rest is counted in the
                      assets test like any other asset, so it does not help the pension.
                    </p>
                  )}

                  {availableToDeploy > 0 && (
                    <ProceedsDeploymentFields factFind={factFind} setFactFind={setFactFind} available={availableToDeploy} value={deployment} onChange={(d: ProceedsDeploymentValue) => setField('deployment', d)} idPrefix="dz" />
                  )}
                </>
              )}
            </div>
          </Card>
          </>)}

          {/* With the funding inputs, not after the suggestion cards: money spent clearing a
              debt is money the rest of the plan cannot use, so the card sits beside the choices
              it competes with. Renders nothing when the client has no debt. */}
          <DebtPayoffStrategy
            factFind={factFind as never}
            debtPayments={debtPayments}
            setDebtPayments={setDebtPayments}
            debtFunding={debtFunding}
            setDebtFunding={setDebtFunding}
            repaymentBasis={debtRepaymentBasis}
            setRepaymentBasis={setDebtRepaymentBasis}
            competingUseNote="The money comes from the same sale proceeds and assets the downsize plan deploys, so what you spend here reduces what is left to allocate."
          />

          {/* Save the Manual Control plan — the engine's Select saves on its own. */}
          {strategyMode === 'manual' && hasDownsize && setSavedStrategies && (
            <Card>
              <h3 className="text-xl font-bold text-gray-800 mb-1">Save this strategy</h3>
              <p className="text-sm text-gray-600 mb-3">Keep this downsizing plan for the dashboard and future comparison.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={dzStrategyName}
                  onChange={(e) => setDzStrategyName(e.target.value)}
                  placeholder='e.g. "Downsize to Woy Woy, top up super"'
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Button
                  variant="brand"
                  onClick={() => {
                    const trimmed = dzStrategyName.trim();
                    if (!trimmed) { setDzSaveNote('Please enter a strategy name first.'); return; }
                    setSavedStrategies((prev) => [
                      ...(prev || []),
                      {
                        id: `dz-${Date.now()}`,
                        name: trimmed,
                        type: 'downsizer',
                        inputs: {
                          details: { ...details },
                          downsizeDate: effectiveDownsizeDate || null,
                          debtPayoff: debtPayoffPlan || null,
                          source: 'manual',
                          seStrategyName: null,
                        },
                        outputs: { agePension: after?.socialSecurity ?? 0, cashFlow: projections[0]?.netCashflow ?? after?.cashFlow ?? 0, legacyEstimate: legacyEstimate ?? 0 },
                        createdAt: new Date().toISOString(),
                      },
                    ]);
                    setDzStrategyName('');
                    setDzSaveNote('Saved — it now appears in the dashboard’s Downsizing section.');
                  }}
                >
                  Save Strategy
                </Button>
              </div>
              {dzSaveNote && <p className="text-sm text-green-700 mt-2">{dzSaveNote}</p>}
            </Card>
          )}

          {strategyMode === 'optimised' && (
            <Card>
              <div className="flex items-center gap-3 mb-1">
                <IconTile icon={TrendingUp} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
                <h3 className="text-xl font-bold text-gray-800">Suggested strategies</h3>
                <button
                  type="button"
                  onClick={() => setDzSetupOpen(true)}
                  className="ml-auto text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline whitespace-nowrap"
                >
                  Adjust setup
                </button>
              </div>
              {!hasDownsize ? (
                <p className="text-sm text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-3 mt-3">
                  Enter the <strong>sale price</strong> in step 1 and I&apos;ll compare the ways to use the money.
                </p>
              ) : needsNewMoneyAnswer ? (
                <NewMoneyProfileGate
                  onAnswer={answerNewMoneyProfile}
                  onSkip={() => setNewMoneySkipped(true)}
                  contextLine="The strategies I'll compare deploy the money your downsize frees up — into savings, super or investments."
                />
              ) : needsTbcAnswer ? (
                <TransferCapGate
                  name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                  onAnswer={answerTbc}
                  onSkip={() => setTbcSkipped(true)}
                />
              ) : needsAnnuityAnswer ? (
                <AnnuityPreferenceGate
                    offerGuaranteed={guaranteedShapeAvailable(factFind as never)}
                    guaranteeWindow={guaranteeWindowWords({
                        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
                        sex: factFind?.client?.sex,
                        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
                        partnerSex: factFind?.partner?.sex,
                        ownerName: factFind?.client?.firstName,
                        partnerName: factFind?.partner?.firstName,
                    })}
                  onAnswer={answerAnnuityPreference}
                  onSkip={() => setAnnuityPrefSkipped(true)}
                  contextLine="Some of the strategies I'll compare use the money your downsize frees up."
                />
              ) : seLoading ? (
                <SuggestionEngineAnalysisLoader
                  title="Optimising your downsizing options"
                  subtitle="We're modelling every way to use the money the sale frees up — this only takes a moment."
                />
              ) : dzStrategies.length === 0 ? (
                <p className="text-sm text-gray-600 mt-3">We couldn&apos;t generate suggestions. Use <strong>Manual Control</strong> to model your options.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mt-1 mb-2">
                    I&apos;ve modelled <strong>{dzStrategies.filter((s) => !isDzBaseline(s) && !isDzSensitivity(s)).length}</strong> ways to {dzSetup.housing === 'rent' ? 'sell, rent and use the money' : dzSetup.housing === 'both' ? 'downsize or rent, and use the money' : 'downsize and use the money'} and picked the strongest for each goal below. Each card carries its own figures, measured against <strong>not downsizing</strong> — the position on the right. <strong>Explain this</strong> sets a plan out in plain words, with the year-by-year detail behind it; <strong>What could go wrong</strong> is the honest list of its risks.
                  </p>
                  <div className="mb-4">
                    <button type="button" onClick={() => setShowAllSeStrategies(true)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                      Compare all {dzStrategies.length} strategies →
                    </button>
                  </div>
                  <GoalChipRow value={optimisationFocus} onChange={setOptimisationFocus} baselineWord="not downsizing" />
                  {optimisationFocus === 'covered' && coveredSameAsEstate && (
                    <div className="mb-4"><SameAsBestEstateNote onShowBestEstate={() => setOptimisationFocus('legacy')} /></div>
                  )}
                  {visibleSeCards.length === 0 && !(optimisationFocus === 'covered' && coveredSameAsEstate) && (
                    <p className="mb-4 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-sm text-slate-700">
                      {emptyGoalLine(optimisationFocus, dzStrategies.filter((s) => !isDzBaseline(s) && !isDzSensitivity(s)), 'not downsizing')}
                    </p>
                  )}
                  <div className="space-y-4">
                    {visibleSeCards.map(({ strategy: leader, badges, belowBaseline }) => {
                      const m = dzMoveOf(leader);
                      const annuity = dzAnnuityOf(leader, factFind);
                      const nilResidual = annuity && Math.round(annuity.residualPercent) === 0 ? annuity : null;
                      const horizonWarning = dzAnnuityHorizonWarningOf(leader, factFind);
                      const base = dzStayPut?.snapshot || {};
                      const snap = leader.snapshot || {};
                      const terms = deploymentAnnuityTerms(leader.inputs?.deployment as ProceedsDeploymentValue | undefined, isCouple);
                      return (
                        <EngineStrategyCard
                          key={leader.id || leader.name}
                          name={leader.name || ''}
                          description={leader.description}
                          badges={badges}
                          belowBaseline={belowBaseline}
                          snapshot={snap}
                          baseline={dzStayPut?.snapshot ?? null}
                          baselineWord="not downsizing"
                          legacyYears={localLegacyYears}
                          flow={flowFor(leader)}
                          goalNote={coveredNoteFor(leader)}
                          inBrief={(
                            <DzInBrief
                              salePrice={m.salePrice}
                              freedCapital={m.freedCapital}
                              buyUpShortfall={m.buyUpShortfall}
                              sellAndRent={m.sellAndRent}
                              rentPerWeek={m.rentPerWeek}
                              downsizerContribution={m.downsizerContribution}
                              pensionBefore={Number(base.pension) || 0}
                              pensionAfter={Number(snap.pension) || 0}
                              cashBefore={Number(base.netCashflow) || 0}
                              cashAfter={Number(snap.netCashflow) || 0}
                              planLegacy={snap.legacyValue ?? null}
                              baseLegacy={base.legacyValue ?? null}
                              legacyYears={localLegacyYears}
                              annuity={annuity}
                            />
                          )}
                          worthKnowing={(nilResidual || horizonWarning) ? (
                            <>
                              {nilResidual && <>The estate ends behind not downsizing mainly because {nilResidualAnnuityText(nilResidual)}. The income is guaranteed for life; the capital is not coming back.{' '}</>}
                              {horizonWarning}
                            </>
                          ) : null}
                          alternative={alternativeAssetNote(leader, dzStrategies, (s) => s.snapshot?.legacyValue ?? null, Number(localLegacyYears) || 5)}
                          moneyRows={leader.deploymentBreakdown && leader.deploymentBreakdown.length > 0 ? { heading: 'Where the proceeds go', rows: leader.deploymentBreakdown } : null}
                          isSelected={leader.id === selectedSeId}
                          onSelect={() => selectEngineStrategy(leader)}
                          onExplain={() => { setExplainSeId(leader.id ?? null); setShowDzExplanation(true); }}
                          onRisks={() => setRiskStrategy(leader)}
                          onGenerateSoa={() => generateDzSoa(leader)}
                        >
                          {/* THE ANNUITY'S TERMS (owner, 2026-09-01): who owns it and whether the
                              income survives them, read off the deployment this card is built from. */}
                          {terms && (
                            <EngineAnnuityTerms
                              className="mt-3"
                              terms={terms}
                              factFind={factFind}
                              quote={<AnnuityQuoteControl
                                draft={dzAnnuityQuoteDraft}
                                onDraft={setDzAnnuityQuoteDraft}
                                committed={dzAnnuityQuote}
                                onCommit={setDzAnnuityQuote}
                              />}
                            />
                          )}
                        </EngineStrategyCard>
                      );
                    })}
                  </div>
                  {/* A plan the horizon rule set aside is said, not hidden — the only note that
                      stays under the cards (owner, 3 Oct 2026: the goals box was more text than
                      understanding; the winners ARE the cards). */}
                  {dzSetAside.length > 0 && (
                    <div className="mt-4 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                      <p><span className="font-semibold">{dzSetAside.length === 1 ? 'One strategy was set aside.' : `${dzSetAside.length} strategies were set aside.`}</span> A plan is not promoted as the best result when the client&apos;s age and health point to a shorter time frame than it needs.</p>
                      <ul className="mt-1 space-y-1">
                        {dzSetAside.map((sa) => (
                          <li key={sa.strategy.id || sa.strategy.name}>
                            <span className="font-semibold">{sa.strategy.name}</span> would have led on {sa.labels.map((l) => labelMap[l] || l).join(' and ')}. {sa.warning}{' '}
                            <button type="button" onClick={() => setSeDeepDive({ projections: sa.strategy.projections || [], name: sa.strategy.name || '', id: sa.strategy.id })} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">See it anyway</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-4">These are model illustrations based on what you entered. To model your own variation, switch to <strong>Manual Control</strong> above.</p>
                </>
              )}
            </Card>
          )}
        </div>

          {/* THE SAME 3/2 SPLIT AS AGED CARE (2026-08-27). `xl:` is deliberate: lg splits far too
              early for a panel this dense, and a 1920 screen at 100% zoom is only a ~1330px viewport. */}
        <div className="xl:col-span-2 space-y-6 xl:sticky xl:top-8 self-start">
          <PlanSummaryPanel
            strategyName={dzPlanSummaryName}
            baselineLabel="not downsizing"
            inBrief={dzInBrief}
            onReadFullExplanation={dzPlanSummaryName ? () => { setExplainSeId(null); setShowDzExplanation(true); } : null}
            onViewProjections={() => (railHoldsStayPut ? setShowStayPutProjections(true) : setSeDeepDive({ projections: panelModel.projections, name: dzPlanSummaryName ?? undefined }))}
            noStrategyHeading={strategyMode === 'optimised' ? 'Not downsizing — for comparison' : 'No plan yet — your position today'}
            noStrategyNote={strategyMode === 'optimised' ? (
              <><span className="font-semibold text-slate-700">For comparison</span> while you choose. Each strategy card carries its own figures —{' '}
              <span className="font-semibold text-slate-700">Select</span> one, then{' '}
              <span className="font-semibold text-slate-700">Explain this</span> and{' '}
              <span className="font-semibold text-slate-700">What could go wrong</span> on the card.</>
            ) : (
              <>
                Nothing has been sold yet. This is your{' '}
                <span className="font-semibold text-slate-700">current position</span> — every figure here is restated against{' '}
                <span className="font-semibold text-slate-700">not downsizing</span> once a plan is built.
              </>
            )}
            costOverviewLabel="What the move costs"
            costOverview={
              <div className="bg-gray-800 text-white rounded-xl p-4">
                <div className={`flex items-start justify-between gap-3 ${hasDownsize ? 'mb-3 pb-3 border-b border-white/15' : ''}`}>
                  <p className="text-gray-400 text-xs leading-snug">{railHoldsStayPut ? <>Net proceeds<br />of the sale</> : <>Money the<br />move frees up</>}</p>
                  <div className="text-right">
                    {/* A COLUMN OF ZEROS IS NOT AN ANSWER. Before a sale price is entered there is
                        no move to price, and "$0 freed up" reads as a finding rather than an empty form. */}
                    {hasDownsize ? (
                      <>
                        <p className="text-3xl font-bold text-white leading-none">{formatCurrency(railHoldsStayPut ? Math.max(0, salePrice - sellingCosts) : panelModel.freedCapital)}</p>
                        <p className="text-gray-300 font-semibold text-xs mt-1">{railHoldsStayPut ? 'after selling costs' : (panelModel.sellAndRent ? 'after costs, renting' : 'after buying and costs')}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-bold text-white leading-tight">No sale price yet</p>
                        <p className="text-gray-400 text-xs mt-1">Enter it in step 1</p>
                      </>
                    )}
                  </div>
                </div>
                {hasDownsize && (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-300">Sale price</span><span className="font-semibold">{formatCurrency(salePrice)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-300">Selling costs</span><span className="font-semibold text-rose-300">−{formatCurrency(sellingCosts)}</span></div>
                  {!railHoldsStayPut && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-300">{panelModel.sellAndRent ? 'New home (renting)' : 'New home'}</span>
                        <span className="font-semibold text-rose-300">{panelModel.sellAndRent ? '—' : `−${formatCurrency(panelModel.newHomePrice)}`}</span>
                      </div>
                      {!panelModel.sellAndRent && stampDuty > 0 && (
                        <div className="flex justify-between"><span className="text-gray-300">Stamp duty</span><span className="font-semibold text-rose-300">−{formatCurrency(stampDuty)}</span></div>
                      )}
                      {panelModel.sellAndRent && (
                        <div className="flex justify-between"><span className="text-gray-300">Rent (a year)</span><span className="font-semibold text-rose-300">{panelModel.rentPerWeek > 0 ? `−${formatCurrency(Math.round(panelModel.rentPerWeek * 52))}` : 'not entered'}</span></div>
                      )}
                      {panelModel.downsizerContribution > 0 && (
                        <div className="flex justify-between pt-3 border-t border-white/15">
                          <span className="text-gray-300">Into super (downsizer)</span>
                          <span className="font-semibold text-green-400">{formatCurrency(panelModel.downsizerContribution)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}
                {railHoldsStayPut && hasDownsize && (
                  <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-600">
                    {strategyMode === 'optimised'
                      ? 'None of this is in the figures below, which are life as it is. Each card says what its new home or rent takes and where the rest goes.'
                      : 'None of this is in the figures below. Choose a mode above to see what the move does.'}
                  </p>
                )}
                {/* A buy-up is not a downsize. Saying so where the figures are, rather than in a
                    note elsewhere, is the difference between a client noticing and not. */}
                {!railHoldsStayPut && panelModel.buyUpShortfall > 0 && (
                  <p className="text-xs text-amber-200 bg-amber-900/30 rounded-lg px-3 py-2 mt-3">
                    The new home costs <strong>{formatCurrency(panelModel.buyUpShortfall)}</strong> more than
                    the sale leaves you. That has to come from savings — this move consumes money rather than
                    freeing it up.
                  </p>
                )}
                {!railHoldsStayPut && panelModel.sellAndRent && (
                  <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-600">
                    Selling without buying frees the most money, but rent becomes a lifelong cost and you no
                    longer hold an exempt home — both are in the figures below.
                  </p>
                )}
              </div>
            }
            liquidity={dzRunway && (
              <>
                <p className="text-lg font-bold leading-tight tracking-tight text-slate-900">{dzRunway.headline}</p>
                <div className="mt-2">
                  <FundingRunway
                    coveredPct={dzRunway.coveredPct}
                    leftLabel={dzRunway.leftLabel}
                    rightLabel={dzRunway.rightLabel}
                    depleted={dzRunway.depleted}
                    flag={dzRunway.flag}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">{dzRunway.basis}</p>
              </>
            )}
            financialImpact={
              <FinancialImpactPanel
                embedded
                /* Not downsizing IS the panel until a plan is named: one figure per row, no change. */
                baselineOnly={railHoldsStayPut}
                before={railBefore}
                after={(railHoldsStayPut ? railBefore : (panelModel.after as FinancialPosition)) ?? railBefore}
                legacyYears={localLegacyYears}
                setLegacyYears={setLocalLegacyYears}
                onViewProjections={() => (railHoldsStayPut ? setShowStayPutProjections(true) : setSeDeepDive({ projections: panelModel.projections, name: dzPlanSummaryName ?? undefined }))}
                projections={railProjections}
                cashFlowAfterValue={railHoldsStayPut ? (railBefore?.cashFlow ?? null) : (panelModel.projections[0]?.netCashflow ?? panelModel.projections[0]?.cashFlow ?? panelModel.after?.cashFlow ?? null)}
                netWorthAfterValue={railHoldsStayPut ? (railBefore?.netWorth ?? null) : (panelModel.after?.netWorth ?? null)}
                legacyEstimateOverride={railHoldsStayPut ? manualBaselineLegacy : (panelModel.legacyEstimate ?? null)}
                /* Measured against NOT downsizing, projected over the same years — not against net
                   worth today, which credits the strategy with growth that happens either way. */
                legacyBaselineValue={manualBaselineLegacy}
                baselineLabel={manualBaselineLegacy != null ? 'not downsizing' : undefined}
              />
            }
          />
        </div>
      </div>
      )}
      </>
      )}

      {/* Every evaluated strategy side by side — nothing the engine compared is hidden behind the
          goal cards. Selecting here works exactly like selecting a card. */}
      {showAllSeStrategies && dzSuggestions && (
        <AllStrategiesModal<DzStrategyView>
          strategies={dzStrategies}
          legacyYears={localLegacyYears}
          selectedId={selectedSeId}
          isBaseline={isDzBaseline}
          isSensitivity={(s) => isDzSensitivity(s)}
          sensitivityHeading="What if the new home cost more, or less?"
          onSelect={selectEngineStrategy}
          onDeepDive={(s) => setSeDeepDive({ projections: s.projections || [], name: s.name || '', id: s.id })}
          onClose={() => setShowAllSeStrategies(false)}
        />
      )}

      {/* "Understanding your plan" — the recap on its own surface; the year-by-year lives in its footer. */}
      {showDzExplanation && (
        <PlanExplanationModal
          strategyName={explainStrategy?.name ?? dzPlanSummaryName}
          onClose={() => { setShowDzExplanation(false); setExplainSeId(null); }}
          footer={(
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowDzExplanation(false);
                if (explainStrategy) setSeDeepDive({ projections: explainStrategy.projections || [], name: explainStrategy.name || '', id: explainStrategy.id });
                else setSeDeepDive({ projections: panelModel.projections, name: dzPlanSummaryName ?? undefined });
                setExplainSeId(null);
              }}
            >
              See the year-by-year figures
            </Button>
          )}
        >
          {dzRecapNode}
        </PlanExplanationModal>
      )}
      {riskStrategy && (
        <StrategyRisksPanel
          strategyName={riskStrategy.name || ''}
          onClose={() => setRiskStrategy(null)}
          risks={buildStrategyRisks(riskKindsFromCandidateId(String(riskStrategy.id || '')), {
            horizonWarning: dzAnnuityHorizonWarningOf(riskStrategy, factFind),
            stampDuty: (Number((riskStrategy.inputs?.deployment as { ipStampDuty?: number } | undefined)?.ipStampDuty) || Number(riskStrategy.inputs?.stampDuty) || null),
          })}
        />
      )}

      {/* Not downsizing, year by year — from step 2 and from the rail while it holds the baseline. */}
      {showStayPutProjections && (
        <ProjectionsModal
          onClose={() => setShowStayPutProjections(false)}
          projections={stayPutProjections as never}
          factFind={factFind as never}
          initialFinancials={(todayFinancials ?? before) as never}
          costLabel={null}
        />
      )}

      {seDeepDive && (
        <ProjectionsModal
          onClose={() => setSeDeepDive(null)}
          projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : seDeepDive.projections) as never}
          {...(deepDiveCandidate ? { onPeriodModeChange: setModalPeriodMode, isReloading: modalPeriodMode === 'calendar_year' && !calendarYearProjections } : {})}
          factFind={factFind as never}
          initialFinancials={(todayFinancials ?? before) as never}
          costLabel={null}
        />
      )}
    </div>
  );
};

interface DownsizerPageProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const DownsizerPage = ({ setActivePage, factFind, setSavedStrategies, setFactFind, onSoaGenerated }: DownsizerPageProps) => {
  const [view, setView] = useState<'explanation' | 'calculator'>('explanation');
  useEffect(() => {
    if (view === 'calculator') {
      scrollAppToTop();
    }
  }, [view]);
  if (view === 'explanation') return <DownsizerExplained setActivePage={setActivePage} onContinue={() => setView('calculator')} />;
  return <DownsizerAnalyser setActivePage={setActivePage} factFind={factFind} onBack={() => setView('explanation')} setSavedStrategies={setSavedStrategies} setFactFind={setFactFind} onSoaGenerated={onSoaGenerated} />;
};

export default DownsizerPage;
